/**
 * Shared price formatting helpers used by the Change Plan view and the
 * plan-change confirmation modal so both surfaces split amounts the same way.
 */

import { getLocale } from "@/utils/locale-store";

/**
 * Splits a major-units decimal (e.g. 32.99) into its integer and 2-digit minor
 * parts for the "32" + "99" superscript layout on tier cards. Minor is null
 * when the amount is a whole number, so the caller can omit the superscript.
 *
 * Edge case: amounts whose minor part rounds to 100 (e.g. 12.999) are not
 * re-rolled into the major part — the function is only called with values
 * that come from configured currency amounts where this doesn't occur.
 */
export function splitMajorUnits(amount: number): {
  major: number;
  minor: string | null;
} {
  const major = Math.trunc(amount);
  const minorCents = Math.round((amount - major) * 100);
  const minor = minorCents > 0 ? String(minorCents).padStart(2, "0") : null;
  return { major, minor };
}

/**
 * Formats a minor-units integer (Stripe convention — 1499 = 14.99) as a
 * fixed 2-decimal string. Used in the plan-change modal for the prorated
 * amount, which the BE returns straight from Stripe's InvoiceCreatePreview.
 * Sign is preserved so callers can render credits as "−" without extra work.
 */
export function formatMinorAsDecimal(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

/**
 * Formats a major-units amount as a localized currency string (e.g. 2519 +
 * "sek" → "2 519,00 kr"). THE single money formatter for the app — every other
 * helper delegates here so there is exactly one Intl call and one fallback
 * shape. Falls back to "<amount> <CODE>" when Intl rejects the currency code,
 * and to a bare 2-decimal number when no currency is known at all.
 *
 * Formats in the SITE's locale, not the browser's: these amounts sit directly
 * beside prices the API already formatted server-side, and a browser default
 * rendered "SEK 486.40" next to "1 199,00 kr / år" on the same Swedish card.
 * Pass `locale` only to override that for a specific surface.
 */
export function formatMajorUnitsAsCurrency(
  amount: number,
  currency: string | null | undefined,
  locale?: string
): string {
  if (!currency) return amount.toFixed(2);
  try {
    return new Intl.NumberFormat(locale ?? getLocale().language, {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }
}

/**
 * Currencies Stripe charges in whole units: the API amount IS the major amount,
 * so 1000 JPY is ¥1000 and not ¥10. Dividing these by 100 under-reports the
 * price by two orders of magnitude.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "BIF",
  "CLP",
  "DJF",
  "GNF",
  "JPY",
  "KMF",
  "KRW",
  "MGA",
  "PYG",
  "RWF",
  "VND",
  "VUV",
  "XAF",
  "XOF",
  "XPF",
]);

/**
 * Currencies whose minor unit is a thousandth (Stripe requires the amount to be
 * divisible by 10, e.g. 1000 KWD minor = 1.000 KWD).
 */
const THREE_DECIMAL_CURRENCIES = new Set([
  "BHD",
  "IQD",
  "JOD",
  "KWD",
  "LYD",
  "OMR",
  "TND",
]);

/**
 * How many API units make one major unit of `currency`, per Stripe's currency
 * conventions. Unknown codes get the two-decimal default, which is right for
 * every currency the shop actually sells in.
 *
 * Stripe's documented special cases (HUF, ISK, TWD, UGX) are deliberately NOT
 * special here: those present as zero-decimal, but the API still takes
 * two-decimal amounts that must divide evenly by 100 — so the default divisor
 * is the correct one for them.
 */
function minorUnitsPerMajor(currency: string | null | undefined): number {
  const code = currency?.toUpperCase() ?? "";
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 1;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 1000;
  return 100;
}

/**
 * Minor-units (Stripe convention — 251900 = 2519.00) wrapper around
 * {@link formatMajorUnitsAsCurrency}. The divisor follows the currency's own
 * minor-unit convention, so zero-decimal currencies (JPY/KRW) and three-decimal
 * ones (KWD/BHD) convert correctly instead of being scaled by a flat 100.
 */
export function formatMinorUnitsAsCurrency(
  amountMinor: number,
  currency: string | null | undefined,
  locale?: string
): string {
  return formatMajorUnitsAsCurrency(
    amountMinor / minorUnitsPerMajor(currency),
    currency,
    locale
  );
}

/**
 * Splits an already-formatted price string into its parts for the
 * "1 199" + "00" superscript layout on tier cards. The currency part is
 * whatever non-digit text sits before or after the amount, so this matches
 * multi-letter names ("1 199,00 kr") as well as symbols ("€85.99", "32.99€") —
 * a symbol-only character class silently failed on every Swedish price.
 *
 * `currencyFirst` says which side the currency belongs on: it leads in en-US
 * ("$32.99") and trails in sv-SE ("1 199,00 kr"). Thousands separators stay in
 * `whole`, normalized to plain spaces so the layout can't inherit a stray NBSP.
 *
 * Returns null when the string isn't a recognizable price, so callers can fall
 * back to rendering it verbatim.
 */
export function splitFormattedPrice(formatted: string): {
  whole: string;
  decimals: string;
  currency: string;
  currencyFirst: boolean;
} | null {
  // Intl groups with NBSP (U+00A0) or narrow NBSP (U+202F) in several locales.
  // Both are in \s, so folding whitespace to a plain space lets one regex cover
  // every locale's grouping, and keeps a stray NBSP out of the rendered parts.
  const trimmed = formatted.replace(/\s+/gu, " ").trim();
  // The decimal marker is the LAST separator before exactly two final digits.
  const match =
    trimmed.match(/^(\D*?) ?(\d[\d .,]*?)[.,](\d{2}) ?(\D*?)$/u) ??
    // Separator-less minor units, e.g. "8599" as "85" + "99" — the shape the
    // old tier-card regex accepted, kept so those values still split.
    trimmed.match(/^(\D*?) ?(\d+?)(\d{2}) ?(\D*?)$/u);
  if (!match) return null;

  const [, prefix = "", whole, decimals, suffix = ""] = match;
  if (!whole || !decimals) return null;

  const prefixCurrency = prefix.trim();
  const suffixCurrency = suffix.trim();
  // A price with no currency text on either side still splits — the layout then
  // renders no glyph, which is what the old regex did too.
  return {
    whole,
    decimals,
    currency: prefixCurrency || suffixCurrency,
    currencyFirst: prefixCurrency.length > 0,
  };
}

interface PriceTemplate {
  prefix: string;
  suffix: string;
  decimal: string;
  decimals: number;
  /** Null when the template amount was too small to show grouping. */
  group: string | null;
}

function parsePriceTemplate(formatted: string): PriceTemplate | null {
  const core = formatted.match(/\d(?:[\d.,'\s]*\d)?/u);
  if (!core || core.index === undefined) return null;
  const number = core[0];
  // The decimal part is the trailing 1–2 digits after the last separator; three
  // trailing digits are a thousands group ("1,234"), not decimals.
  const decimalMatch = number.match(/([.,])(\d{1,2})$/);
  const integerPart = decimalMatch
    ? number.slice(0, -decimalMatch[0].length)
    : number;
  const groupMatch = integerPart.match(/\D/u);
  return {
    prefix: formatted.slice(0, core.index),
    suffix: formatted.slice(core.index + number.length),
    decimal: decimalMatch?.[1] ?? ".",
    decimals: decimalMatch?.[2]?.length ?? 0,
    group: groupMatch?.[0] ?? null,
  };
}

/**
 * Formats a major-units amount the way the backend formatted `templates`. The
 * API formats prices in the store currency's culture (Umbraco Commerce), which
 * the site locale doesn't tell us — so an amount the client computes itself (a
 * total re-derived for the picked shipping method) rendered via Intl as
 * "€1,398.00" right next to the server's "1 398,00 €". Copying the currency
 * placement, decimal mark and group separator from the server's own strings
 * keeps them identical. The grouping comes from the first template large enough
 * to show one. Falls back to {@link formatMajorUnitsAsCurrency} when no template
 * is recognizable.
 */
export function formatMajorUnitsLike(
  amount: number,
  templates: (string | null | undefined)[],
  currency: string | null | undefined
): string {
  const parsed = templates
    .filter((t): t is string => !!t)
    .map(parsePriceTemplate)
    .filter((t): t is PriceTemplate => t !== null);
  const template = parsed[0];
  if (!template) return formatMajorUnitsAsCurrency(amount, currency);
  const group = parsed.find((t) => t.group !== null)?.group ?? null;

  const [whole = "", fraction] = Math.abs(amount)
    .toFixed(template.decimals)
    .split(".");
  const grouped = group ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, group) : whole;
  const number = fraction
    ? `${grouped}${template.decimal}${fraction}`
    : grouped;
  return `${amount < 0 ? "-" : ""}${template.prefix}${number}${template.suffix}`;
}

/**
 * Formats a major-units decimal (e.g. 32.99) as a fixed 2-decimal string for
 * inline display rather than the split major/minor layout. Used for the plan
 * comparison rows in the modal.
 */
export function formatMajorAsDecimal(amount: number): string {
  return amount.toFixed(2);
}

/**
 * Parses a formatted money string (e.g. "699,00 €", "1.234,56 €", "1,234.56")
 * to a major-units number, or null when absent/unparseable. The API returns
 * amounts pre-formatted for the active locale, so the decimal marker may be a
 * comma or a dot and thousands separators may be present. We treat the
 * rightmost separator as the decimal marker and drop every other separator,
 * which handles both EU ("1.234,56") and US ("1,234.56") groupings.
 *
 * Separators that don't sit between digits are dropped first: Danish formats
 * the currency as "kr.", and that trailing dot taken as the decimal marker
 * turned "3.399,00 kr." into 339900.
 */
export function parseFormattedAmount(formatted: string | null): number | null {
  if (!formatted) return null;
  const cleaned = formatted
    .replace(/[^\d.,]/g, "")
    .replace(/^[.,]+|[.,]+$/g, "");
  const lastSep = Math.max(cleaned.lastIndexOf(","), cleaned.lastIndexOf("."));
  const normalized =
    lastSep === -1
      ? cleaned
      : cleaned.slice(0, lastSep).replace(/[.,]/g, "") +
        "." +
        cleaned.slice(lastSep + 1).replace(/[.,]/g, "");
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : null;
}
